import React from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import type {
  ContentCardData,
  ContentUploadWindow,
} from "../../../@types/portalAluno";
import ContentCardList from "../../../Components/ContentCardList/ContentCardList";
import ContentCardsSkeleton from "../../../Components/ContentCardsSkeleton/ContentCardsSkeleton";
import ContentTopics, {
  EMPTY_TAGS,
  EMPTY_TOPICS,
} from "../../../Components/ContentTopics/ContentTopics";
import PageHeader from "../../../Components/PageHeader/PageHeader";
import { getContentSection } from "../../../config/portalAluno/contentSections";
import ContentListingPageSkeleton from "./ContentListingPageSkeleton";
import useContentListing from "../../../hooks/PortalAluno/useContentListing";
import useContentTags from "../../../hooks/PortalAluno/useContentTags";
import useContentTopics from "../../../hooks/PortalAluno/useContentTopics";
import useTagContents from "../../../hooks/PortalAluno/useTagContents";
import useTopicContents from "../../../hooks/PortalAluno/useTopicContents";
import {
  buildListingRoute,
  readTagParam,
  readTopicIdParam,
  readUploadWindowParam,
} from "../../../utils/portalAluno/listingFilters";
import "./styles.css";

const dedupeCards = (pages: { cards: ContentCardData[] }[]) => {
  const seenCards = new Set<string>();

  return pages.flatMap((page) =>
    page.cards.filter((card) => {
      const key = `${card.sourceType ?? "conteudo"}-${card.id}`;

      if (seenCards.has(key)) return false;
      seenCards.add(key);
      return true;
    })
  );
};

const getErrorMessage = (error: unknown) =>
  error instanceof Error && error.message && error.message !== "Network Error"
    ? error.message
    : "Não foi possível carregar a listagem. Verifique sua conexão e tente novamente.";

const ContentListingPage = () => {
  const { contentType } = useParams<{ contentType: string }>();
  const section = getContentSection(contentType);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTopicId = readTopicIdParam(location.search);
  const uploadWindow = readUploadWindowParam(location.search);
  const selectedTag = readTagParam(location.search);
  const isTagFiltered = Boolean(section?.tagFiltered);

  const applyFilters = React.useCallback(
    (filters: {
      topicId?: number | null;
      uploadWindow?: ContentUploadWindow | null;
    }) => {
      navigate(
        buildListingRoute(location.pathname, {
          topicId: filters.topicId,
          uploadWindow: filters.uploadWindow,
        }),
        { replace: true }
      );
    },
    [location.pathname, navigate]
  );

  const handleTopicChange = React.useCallback(
    (topicId: number | null) => applyFilters({ topicId, uploadWindow }),
    [applyFilters, uploadWindow]
  );

  const handleTagChange = React.useCallback(
    (tag: string | null) =>
      navigate(
        buildListingRoute(location.pathname, {
          tag,
          topicId: selectedTopicId,
        }),
        { replace: true }
      ),
    [location.pathname, navigate, selectedTopicId]
  );

  const handleUploadWindowChange = React.useCallback(
    (nextUploadWindow: ContentUploadWindow | null) =>
      applyFilters({ topicId: selectedTopicId, uploadWindow: nextUploadWindow }),
    [applyFilters, selectedTopicId]
  );

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useContentListing(section, isTagFiltered ? null : uploadWindow);
  const topics = useContentTopics(section);
  const topicContents = useTopicContents(section, selectedTopicId);
  const tags = useContentTags(section);
  const tagContents = useTagContents(section, selectedTag);

  const listingCards = React.useMemo(
    () => dedupeCards(data?.pages ?? []),
    [data]
  );

  const { refetch: refetchTopics } = topics;
  const handleTopicsRetry = React.useCallback(() => {
    refetchTopics();
  }, [refetchTopics]);

  const isFilteringByTopic = Boolean(selectedTopicId);
  const isFilteringByTag = isTagFiltered && Boolean(selectedTag);
  const topicPageCards = React.useMemo(
    () => dedupeCards(topicContents.data?.pages ?? []),
    [topicContents.data]
  );
  const tagPageCards = React.useMemo(
    () => dedupeCards(tagContents.data?.pages ?? []),
    [tagContents.data]
  );
  const cards = isFilteringByTag
    ? tagPageCards
    : isFilteringByTopic
    ? topicPageCards
    : listingCards;
  const pagination = isFilteringByTag
    ? {
        fetchNextPage: tagContents.fetchNextPage,
        hasError: tagContents.isError && Boolean(tagContents.data),
        hasNextPage: tagContents.hasNextPage,
        isFetchingNextPage: tagContents.isFetchingNextPage,
      }
    : isFilteringByTopic
    ? {
        fetchNextPage: topicContents.fetchNextPage,
        hasError: topicContents.isError && Boolean(topicContents.data),
        hasNextPage: topicContents.hasNextPage,
        isFetchingNextPage: topicContents.isFetchingNextPage,
      }
    : {
        fetchNextPage,
        hasError: isError && Boolean(data),
        hasNextPage,
        isFetchingNextPage,
      };

  if (!section) {
    return (
      <section
        className="content-page content-listing-page"
        aria-label="Conteúdos"
      >
        <div className="content-listing-page__feedback" role="status">
          <h1>Seção não encontrada</h1>
          <p>A listagem solicitada não está disponível.</p>
          <button type="button" onClick={() => navigate("/app")}>
            Voltar para a Home
          </button>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section
        className="content-page content-listing-page"
        aria-label={`Carregando ${section.title}`}
      >
        <ContentListingPageSkeleton />
      </section>
    );
  }

  const renderContents = () => {
    if (isFilteringByTopic && topicContents.isLoading) {
      return <ContentCardsSkeleton count={6} showTitle={false} />;
    }

    if (isFilteringByTopic && topicContents.isError && !topicContents.data) {
      return (
        <div className="content-listing-page__feedback" role="alert">
          <h1>Não foi possível filtrar por este tema</h1>
          <p>{getErrorMessage(topicContents.error)}</p>
          <button type="button" onClick={() => topicContents.refetch()}>
            Tentar novamente
          </button>
        </div>
      );
    }

    return (
      <>
        <ContentCardList
          title={section.title}
          cards={cards}
          hideTitle
        />

        {!uploadWindow && pagination.hasNextPage && (
          <div className="content-listing-page__pagination">
            {pagination.isFetchingNextPage ? (
              <div aria-label="Carregando mais conteúdos" aria-busy="true">
                <ContentCardsSkeleton count={3} showTitle={false} />
              </div>
            ) : pagination.hasError ? (
              <div className="content-listing-page__pagination-error" role="alert">
                <p>Não foi possível carregar mais conteúdos.</p>
                <button type="button" onClick={() => pagination.fetchNextPage()}>
                  Tentar novamente
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => pagination.fetchNextPage()}>
                Carregar mais
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <section
      className="content-page content-listing-page"
      aria-label={section.title}
    >
      <div className="content-listing-page__inner">
        <button
          type="button"
          className="content-listing-page__back"
          onClick={() => navigate("/app")}
        >
          <ArrowBackRoundedIcon aria-hidden="true" />
          <span>Voltar para a Home</span>
        </button>

        <ContentTopics
          topics={topics.data ?? EMPTY_TOPICS}
          selectedTopicId={selectedTopicId}
          onTopicChange={handleTopicChange}
          isLoading={topics.isLoading}
          isError={topics.isError}
          onRetry={handleTopicsRetry}
          selectedUploadWindow={isTagFiltered ? null : uploadWindow}
          onUploadWindowChange={isTagFiltered ? undefined : handleUploadWindowChange}
          tags={isTagFiltered ? tags.data ?? EMPTY_TAGS : undefined}
          selectedTag={selectedTag}
          onTagChange={isTagFiltered ? handleTagChange : undefined}
        />

        <PageHeader
          title={section.title}
          description={section.description}
        />

        {isError && !data && !isFilteringByTopic ? (
          <div className="content-listing-page__feedback" role="alert">
            <h1>Não foi possível abrir esta listagem</h1>
            <p>{getErrorMessage(error)}</p>
            <button type="button" onClick={() => refetch()}>
              Tentar novamente
            </button>
          </div>
        ) : (
          renderContents()
        )}
      </div>
    </section>
  );
};

export default ContentListingPage;
